import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Rating,
  Divider,
  Paper,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Verified,
  LocationOn,
  Schedule,
  AttachMoney,
  Star,
  Phone,
  Email,
  Share,
  Favorite,
  FavoriteBorder,
  CalendarToday,
  WorkOutline,
  School,
  EmojiEvents,
  PhotoCamera,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';

const ProfessionalProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [bookingData, setBookingData] = useState({
    service: '',
    date: '',
    time: '',
    description: '',
    address: '',
  });
  const [reviewData, setReviewData] = useState({
    rating: 5,
    comment: '',
  });

  useEffect(() => {
    fetchProfessional();
    checkFavoriteStatus();
  }, [id]);

  const fetchProfessional = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/professionals/${id}`);
      if (response.data.success) {
        setProfessional(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching professional:', error);
      showError('Error al cargar el perfil del profesional');
      // Mock data for development
      setProfessional(mockProfessional);
    } finally {
      setLoading(false);
    }
  };

  const checkFavoriteStatus = async () => {
    if (!user) return;
    try {
      const response = await axios.get(`/api/users/favorites/${id}`);
      setIsFavorite(response.data.isFavorite);
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      showError('Debes iniciar sesión para agregar favoritos');
      return;
    }
    
    try {
      if (isFavorite) {
        await axios.delete(`/api/users/favorites/${id}`);
        showSuccess('Eliminado de favoritos');
      } else {
        await axios.post(`/api/users/favorites/${id}`);
        showSuccess('Agregado a favoritos');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      showError('Error al actualizar favoritos');
    }
  };

  const handleBooking = async () => {
    if (!user) {
      showError('Debes iniciar sesión para reservar un servicio');
      return;
    }
    
    try {
      const response = await axios.post('/api/bookings', {
        professionalId: id,
        ...bookingData,
      });
      
      if (response.data.success) {
        showSuccess('Reserva creada exitosamente');
        setBookingDialog(false);
        navigate('/dashboard/bookings');
      }
    } catch (error) {
      showError('Error al crear la reserva');
    }
  };

  const handleReview = async () => {
    if (!user) {
      showError('Debes iniciar sesión para dejar una reseña');
      return;
    }
    
    try {
      const response = await axios.post('/api/reviews', {
        professionalId: id,
        ...reviewData,
      });
      
      if (response.data.success) {
        showSuccess('Reseña enviada exitosamente');
        setReviewDialog(false);
        fetchProfessional(); // Refresh data
      }
    } catch (error) {
      showError('Error al enviar la reseña');
    }
  };

  const shareProfile = () => {
    if (navigator.share) {
      navigator.share({
        title: `${professional.name} - Jooru`,
        text: `Conoce a ${professional.name}, profesional en ${professional.services.join(', ')}`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      showSuccess('Enlace copiado al portapapeles');
    }
  };

  // Mock data for development
  const mockProfessional = {
    id: 1,
    name: 'María González',
    email: 'maria@example.com',
    phone: '+52 55 1234 5678',
    image: '/api/placeholder/200/200',
    verified: true,
    rating: 4.9,
    reviewCount: 127,
    location: 'Bogotá, Colombia',
    joinDate: '2022-01-15',
    hourlyRate: 25,
    responseTime: '2 horas',
    completedJobs: 156,
    bio: 'Profesional en limpieza del hogar con más de 8 años de experiencia. Me especializo en limpieza profunda, organización y mantenimiento de espacios residenciales. Trabajo con productos ecológicos y técnicas especializadas.',
    services: [
      {
        id: 1,
        name: 'Limpieza General del Hogar',
        price: 250,
        duration: '3-4 horas',
        description: 'Limpieza completa de todas las áreas del hogar',
      },
      {
        id: 2,
        name: 'Limpieza Profunda',
        price: 400,
        duration: '5-6 horas',
        description: 'Limpieza detallada incluyendo áreas difíciles',
      },
    ],
    skills: ['Limpieza Ecológica', 'Organización', 'Limpieza de Alfombras', 'Limpieza de Ventanas'],
    certifications: [
      'Certificación en Limpieza Profesional',
      'Curso de Productos Ecológicos',
      'Primeros Auxilios',
    ],
    availability: {
      monday: ['09:00', '17:00'],
      tuesday: ['09:00', '17:00'],
      wednesday: ['09:00', '17:00'],
      thursday: ['09:00', '17:00'],
      friday: ['09:00', '17:00'],
      saturday: ['10:00', '15:00'],
      sunday: [],
    },
    gallery: [
      '/api/placeholder/300/200',
      '/api/placeholder/300/200',
      '/api/placeholder/300/200',
      '/api/placeholder/300/200',
    ],
    reviews: [
      {
        id: 1,
        user: 'Ana Pérez',
        rating: 5,
        comment: 'Excelente servicio, muy profesional y detallista.',
        date: '2024-01-15',
        service: 'Limpieza General del Hogar',
      },
      {
        id: 2,
        user: 'Carlos Ruiz',
        rating: 5,
        comment: 'Muy recomendable, dejó mi casa impecable.',
        date: '2024-01-10',
        service: 'Limpieza Profunda',
      },
      {
        id: 3,
        user: 'Laura Martín',
        rating: 4,
        comment: 'Buen trabajo, puntual y eficiente.',
        date: '2024-01-05',
        service: 'Limpieza General del Hogar',
      },
    ],
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!professional) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          Profesional no encontrado
        </Typography>
        <Button variant="contained" onClick={() => navigate('/services')}>
          Volver a Servicios
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={professional.image}
                  alt={professional.name}
                  sx={{
                    width: 150,
                    height: 150,
                    border: 4,
                    borderColor: 'primary.main',
                    mb: 2,
                  }}
                />
                {professional.verified && (
                  <Verified
                    color="primary"
                    sx={{
                      position: 'absolute',
                      bottom: 16,
                      right: 8,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      fontSize: 32,
                    }}
                  />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                {professional.name}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={professional.rating} readOnly precision={0.1} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {professional.rating}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  ({professional.reviewCount} reseñas)
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <LocationOn color="action" />
                <Typography variant="body1">{professional.location}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AttachMoney color="success" />
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                  ${professional.hourlyRate?.toLocaleString('es-CO')}/servicio
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Schedule color="action" />
                <Typography variant="body1">
                  Responde en {professional.responseTime}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {professional.services.map((service) => (
                  <Chip
                    key={service.id || service.name}
                    label={service.name || service}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => setBookingDialog(true)}
                  sx={{ borderRadius: 2 }}
                >
                  Reservar Servicio
                </Button>
                
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}>
                    <IconButton onClick={toggleFavorite} color="primary">
                      {isFavorite ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Compartir perfil">
                    <IconButton onClick={shareProfile} color="primary">
                      <Share />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Contactar">
                    <IconButton href={`tel:${professional.phone}`} color="primary">
                      <Phone />
                    </IconButton>
                  </Tooltip>
                </Box>
                
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    📊 {professional.completedJobs} trabajos completados
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    📅 Miembro desde {new Date(professional.joinDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Sobre mí" />
          <Tab label="Servicios" />
          <Tab label="Reseñas" />
          <Tab label="Galería" />
        </Tabs>

        <Box sx={{ p: 4 }}>
          {/* About Tab */}
          {tabValue === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Acerca de mí
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {professional.bio}
                  </Typography>
                  
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
                    Habilidades
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                    {professional.skills.map((skill) => (
                      <Chip key={skill} label={skill} variant="outlined" />
                    ))}
                  </Box>
                  
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Certificaciones
                  </Typography>
                  <List>
                    {professional.certifications.map((cert, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <EmojiEvents />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText primary={cert} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Disponibilidad
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {Object.entries(professional.availability).map(([day, hours]) => (
                      <Box key={day} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {day === 'monday' && 'Lunes'}
                          {day === 'tuesday' && 'Martes'}
                          {day === 'wednesday' && 'Miércoles'}
                          {day === 'thursday' && 'Jueves'}
                          {day === 'friday' && 'Viernes'}
                          {day === 'saturday' && 'Sábado'}
                          {day === 'sunday' && 'Domingo'}
                        </Typography>
                        <Typography variant="body2" color={hours.length ? 'text.primary' : 'text.secondary'}>
                          {hours.length ? `${hours[0]} - ${hours[1]}` : 'No disponible'}
                        </Typography>
                      </Box>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            </motion.div>
          )}

          {/* Services Tab */}
          {tabValue === 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Grid container spacing={3}>
                {professional.services.map((service) => (
                  <Grid item xs={12} md={6} key={service.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {service.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {service.description}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney color="success" />
                            <Typography variant="h6" color="success.main" sx={{ fontWeight: 'bold' }}>
                              ${service.price}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {service.duration}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Button
                          variant="outlined"
                          fullWidth
                          sx={{ mt: 2 }}
                          onClick={() => {
                            setBookingData(prev => ({ ...prev, service: service.name }));
                            setBookingDialog(true);
                          }}
                        >
                          Reservar este servicio
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}

          {/* Reviews Tab */}
          {tabValue === 2 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Reseñas ({professional.reviewCount})
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setReviewDialog(true)}
                  disabled={!user}
                >
                  Escribir reseña
                </Button>
              </Box>
              
              <List>
                {professional.reviews.map((review, index) => (
                  <React.Fragment key={review.id}>
                    <ListItem alignItems="flex-start">
                      <ListItemAvatar>
                        <Avatar>{review.user.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {review.user}
                            </Typography>
                            <Rating value={review.rating} readOnly size="small" />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(review.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                              {review.service}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {review.comment}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                    {index < professional.reviews.length - 1 && <Divider variant="inset" component="li" />}
                  </React.Fragment>
                ))}
              </List>
            </motion.div>
          )}

          {/* Gallery Tab */}
          {tabValue === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                Galería de trabajos
              </Typography>
              <Grid container spacing={2}>
                {professional.gallery.map((image, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card>
                      <CardContent sx={{ p: 0 }}>
                        <Box
                          component="img"
                          src={image}
                          alt={`Trabajo ${index + 1}`}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          )}
        </Box>
      </Paper>

      {/* Booking Dialog */}
      <Dialog open={bookingDialog} onClose={() => setBookingDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Reservar Servicio</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Servicio</InputLabel>
                <Select
                  value={bookingData.service}
                  onChange={(e) => setBookingData(prev => ({ ...prev, service: e.target.value }))}
                  label="Servicio"
                >
                  {professional.services.map((service) => (
                    <MenuItem key={service.id} value={service.name}>
                      {service.name} - ${service.price}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha"
                type="date"
                value={bookingData.date}
                onChange={(e) => setBookingData(prev => ({ ...prev, date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hora"
                type="time"
                value={bookingData.time}
                onChange={(e) => setBookingData(prev => ({ ...prev, time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Dirección"
                value={bookingData.address}
                onChange={(e) => setBookingData(prev => ({ ...prev, address: e.target.value }))}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción adicional"
                multiline
                rows={3}
                value={bookingData.description}
                onChange={(e) => setBookingData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe cualquier detalle específico sobre el servicio..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookingDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleBooking}>
            Confirmar Reserva
          </Button>
        </DialogActions>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onClose={() => setReviewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Escribir Reseña</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Calificación
            </Typography>
            <Rating
              value={reviewData.rating}
              onChange={(e, newValue) => setReviewData(prev => ({ ...prev, rating: newValue }))}
              size="large"
            />
            
            <TextField
              fullWidth
              label="Comentario"
              multiline
              rows={4}
              value={reviewData.comment}
              onChange={(e) => setReviewData(prev => ({ ...prev, comment: e.target.value }))}
              placeholder="Comparte tu experiencia con este profesional..."
              sx={{ mt: 3 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleReview}>
            Enviar Reseña
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfessionalProfile;